/**
 * @fileoverview Contact-field placeholder codec.
 *
 * A "contact field" is a template placeholder that binds explicitly to a
 * contact record rather than to job/org data. Unlike the legacy
 * `recipient_*` keys (which are filled implicitly by `cr:*` fan-out plus
 * silent fallbacks in `mergeFieldValues`), a contact field declares
 * exactly which contact domain and data part it pulls from.
 *
 * There are TWO families of contact fields:
 *
 * 1. Basic contact-record fields — `contact_<domain>_<part>` — the name,
 *    email, phone, address, ABN, ACN that every `jobContacts` /
 *    `firmContacts` row carries. One value per contact.
 *
 *      contact_<domain>_<part>
 *
 *      domain — "job"  → resolved from `jobContacts`  (per-job, agent-sourced)
 *               "firm" → resolved from `firmContacts` (org-wide directory)
 *      part   — which piece of the contact record to inject (see PARTS below)
 *
 * 2. Source-data fields — `contact_<domain>_<source_kind>_<attribute>` —
 *    the agent-derived data linked to a job contact via its `sourceLinks`
 *    (security interests, employee entitlements, loans, liabilities,
 *    ATO/SRO, corporate-structure rows, related parties, asset
 *    valuations). A contact can be linked to MULTIPLE source rows of the
 *    same kind (e.g. one secured creditor with three PPSR registrations),
 *    so generation fans out one document per `(contact × source link)`
 *    pair. V1 only exposes source fields for the `job` domain; firm
 *    contacts don't carry per-job source data.
 *
 *      contact_<domain>_<source_kind>_<attribute>
 *
 * Both families produce snake_case keys (no colons) so they flow through
 * the same placeholder-substitution regexes as every canonical key —
 * `replaceFieldPlaceholders`, the TipTap field chip, PDF mappings, etc.
 * all treat them identically to `company_name`.
 *
 * WHICH contact a field resolves to is decided at the template level: the
 * Settings tab tags the template with job-contact roles (`cr:*`) and/or
 * firm-contact categories (`fc:*`), and generation fans out one document
 * per matching contact. A `contact_job_name` field then fills with each
 * fan-out recipient's name — no per-role configuration needed. Source
 * fields go one level deeper: when the template body references any
 * `contact_job_<source_kind>_*` key, the wizard fans out per matching
 * source link instead of just per contact.
 *
 * Shared by the template editor (frontend) and the generation pipeline
 * (Convex) — keep this module dependency-free.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ContactFieldDomain = "job" | "firm";

/** Which piece of a contact record a contact field injects. */
export type ContactFieldPart =
  | "name"
  | "first_name"
  | "middle_name"
  | "last_name"
  | "full_name"
  | "date_of_birth"
  | "email"
  | "phone"
  | "abn"
  | "acn"
  | "address_full"
  | "address_line1"
  | "address_line2"
  | "address_suburb"
  | "address_state"
  | "address_postcode"
  // Primary contact person (company ↔ contact-person link). Job domain only —
  // resolved from the company contact's designated primary person.
  | "primary_contact_name"
  | "primary_contact_first_name"
  | "primary_contact_middle_name"
  | "primary_contact_last_name"
  | "primary_contact_full_name"
  | "primary_contact_email"
  | "primary_contact_phone"
  // External administration overlay. Job domain only — resolved from the
  // company contact's linked appointee contact (and that appointee's firm).
  | "name_with_status"
  | "external_admin_status"
  | "external_admin_appointee"
  | "external_admin_appointee_title"
  | "external_admin_appointee_email"
  | "external_admin_appointee_phone"
  | "external_admin_firm"
  | "external_admin_firm_address";

export interface ContactFieldKey {
  domain: ContactFieldDomain;
  part: ContactFieldPart;
}

// ---------------------------------------------------------------------------
// Part metadata
// ---------------------------------------------------------------------------

interface ContactFieldPartMeta {
  part: ContactFieldPart;
  /** Human label shown in the Insert Field menu. */
  label: string;
  /** Short noun used to build a placeholder's display label. */
  shortLabel: string;
  /**
   * Domains this part is offered for. Omitted = both `job` and `firm`. The
   * person-only parts (split name, DOB) are scoped to `["job"]` so the
   * firm-directory picker doesn't surface bankrupt-only fields. See MOR-562.
   */
  domains?: ReadonlyArray<ContactFieldDomain>;
}

/** Every bindable part, in display order. */
export const CONTACT_FIELD_PARTS: ReadonlyArray<ContactFieldPartMeta> = [
  { part: "name", label: "Full Name", shortLabel: "Full Name" },
  // Person-name parts (MOR-643). Scoped to the job domain — these only make
  // sense for a person job contact (the firm directory picker shouldn't offer
  // first/middle/last). "Name" is the canonical First + Middle + Last
  // (MOR-976), so it and "Full Name" agree for person contacts.
  {
    part: "first_name",
    label: "First Name",
    shortLabel: "First Name",
    domains: ["job"],
  },
  {
    part: "middle_name",
    label: "Middle Name",
    shortLabel: "Middle Name",
    domains: ["job"],
  },
  {
    part: "last_name",
    label: "Last Name",
    shortLabel: "Last Name",
    domains: ["job"],
  },
  {
    part: "full_name",
    label: "Full Name (First Middle Last)",
    shortLabel: "Full Name",
    domains: ["job"],
  },
  {
    part: "date_of_birth",
    label: "Date of Birth",
    shortLabel: "Date of Birth",
    domains: ["job"],
  },
  { part: "email", label: "Email", shortLabel: "Email" },
  { part: "phone", label: "Phone", shortLabel: "Phone" },
  {
    part: "address_full",
    label: "Address (full block)",
    shortLabel: "Address",
  },
  {
    part: "address_line1",
    label: "Address — Line 1",
    shortLabel: "Address Line 1",
  },
  {
    part: "address_line2",
    label: "Address — Line 2",
    shortLabel: "Address Line 2",
  },
  { part: "address_suburb", label: "Address — Suburb", shortLabel: "Suburb" },
  { part: "address_state", label: "Address — State", shortLabel: "State" },
  {
    part: "address_postcode",
    label: "Address — Postcode",
    shortLabel: "Postcode",
  },
  { part: "abn", label: "ABN", shortLabel: "ABN" },
  { part: "acn", label: "ACN", shortLabel: "ACN" },
  // Primary contact person — the person linked to a company contact and marked
  // as its primary. Job domain only (companies live in the job CRM); the firm
  // directory picker shouldn't offer these.
  {
    part: "primary_contact_name",
    label: "Primary Contact — Full Name",
    shortLabel: "Contact Name",
    domains: ["job"],
  },
  {
    part: "primary_contact_first_name",
    label: "Primary Contact — First Name",
    shortLabel: "Contact First Name",
    domains: ["job"],
  },
  {
    part: "primary_contact_middle_name",
    label: "Primary Contact — Middle Name",
    shortLabel: "Contact Middle Name",
    domains: ["job"],
  },
  {
    part: "primary_contact_last_name",
    label: "Primary Contact — Last Name",
    shortLabel: "Contact Last Name",
    domains: ["job"],
  },
  {
    part: "primary_contact_full_name",
    label: "Primary Contact — Full Name (First Middle Last)",
    shortLabel: "Contact Full Name",
    domains: ["job"],
  },
  {
    part: "primary_contact_email",
    label: "Primary Contact — Email",
    shortLabel: "Contact Email",
    domains: ["job"],
  },
  {
    part: "primary_contact_phone",
    label: "Primary Contact — Phone",
    shortLabel: "Contact Phone",
    domains: ["job"],
  },
  // External administration overlay. Job domain only. The appointee is a
  // linked job contact (person or firm); "Firm" is the appointee's linked
  // company, or the appointee itself when it is an organisation. The ordinary
  // Name/Address parts above stay the company's own — a letter to the
  // appointee binds these parts for its address block.
  {
    part: "name_with_status",
    label: "Name with External Administration Status",
    shortLabel: "Name (Status)",
    domains: ["job"],
  },
  {
    part: "external_admin_status",
    label: "External Administration — Status",
    shortLabel: "Ext Admin Status",
    domains: ["job"],
  },
  {
    part: "external_admin_appointee",
    label: "External Administration — Appointee",
    shortLabel: "Ext Admin Appointee",
    domains: ["job"],
  },
  {
    part: "external_admin_appointee_title",
    label: "External Administration — Appointee Title",
    shortLabel: "Ext Admin Title",
    domains: ["job"],
  },
  {
    part: "external_admin_appointee_email",
    label: "External Administration — Appointee Email",
    shortLabel: "Ext Admin Email",
    domains: ["job"],
  },
  {
    part: "external_admin_appointee_phone",
    label: "External Administration — Appointee Phone",
    shortLabel: "Ext Admin Phone",
    domains: ["job"],
  },
  {
    part: "external_admin_firm",
    label: "External Administration — Firm",
    shortLabel: "Ext Admin Firm",
    domains: ["job"],
  },
  {
    part: "external_admin_firm_address",
    label: "External Administration — Firm Address",
    shortLabel: "Ext Admin Firm Address",
    domains: ["job"],
  },
];

const PART_SET: ReadonlySet<string> = new Set(
  CONTACT_FIELD_PARTS.map((p) => p.part),
);

function isContactFieldPart(part: string): part is ContactFieldPart {
  return PART_SET.has(part);
}

const PART_META_BY_KEY: Record<ContactFieldPart, ContactFieldPartMeta> =
  Object.fromEntries(CONTACT_FIELD_PARTS.map((p) => [p.part, p])) as Record<
    ContactFieldPart,
    ContactFieldPartMeta
  >;

/** True when a part is offered for the given domain (default = all domains). */
export function contactFieldPartAppliesToDomain(
  meta: ContactFieldPartMeta,
  domain: ContactFieldDomain,
): boolean {
  return !meta.domains || meta.domains.includes(domain);
}

/** Bindable parts offered for a domain, in display order. */
export function contactFieldPartsForDomain(
  domain: ContactFieldDomain,
): ReadonlyArray<ContactFieldPartMeta> {
  return CONTACT_FIELD_PARTS.filter((p) =>
    contactFieldPartAppliesToDomain(p, domain),
  );
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/**
 * Prefix that marks a field key as a contact-field binding. A real
 * contact key always continues with `job_` or `firm_` after this — so
 * the legacy canonical keys `contact_name` / `contact_email` /
 * `contact_phone` (the firm contact-person fields) are NOT contact
 * fields and parse to `null`.
 */
export const CONTACT_FIELD_PREFIX = "contact_" as const;

/** Build the canonical `contact_<domain>_<part>` key. */
export function buildContactFieldKey(binding: ContactFieldKey): string {
  return `${CONTACT_FIELD_PREFIX}${binding.domain}_${binding.part}`;
}

/**
 * Splits a `contact_`-stripped tail into its `job_`/`firm_` domain and the
 * remainder, or `null` when neither prefix matches. Shared by every
 * `contact_<domain>_...` codec below.
 */
function splitContactDomain(
  rest: string,
): { domain: ContactFieldDomain; after: string } | null {
  if (rest.startsWith("job_")) {
    return { domain: "job", after: rest.slice("job_".length) };
  }
  if (rest.startsWith("firm_")) {
    return { domain: "firm", after: rest.slice("firm_".length) };
  }
  return null;
}

/**
 * Parse a field key back into its contact binding. Returns `null` for any
 * key that isn't a well-formed contact field — callers treat that as "not
 * a contact field" and fall through to canonical / custom handling.
 */
export function parseContactFieldKey(key: string): ContactFieldKey | null {
  if (!key.startsWith(CONTACT_FIELD_PREFIX)) return null;
  const rest = key.slice(CONTACT_FIELD_PREFIX.length);
  const split = splitContactDomain(rest);
  if (!split) return null;
  const { domain, after: part } = split;
  if (!isContactFieldPart(part)) return null;
  return { domain, part };
}

/** True when a field key is a contact-field binding. */
export function isContactFieldKey(key: string): boolean {
  return parseContactFieldKey(key) !== null;
}

/** Metadata for a single part. */
export function getContactFieldPartMeta(
  part: ContactFieldPart,
): ContactFieldPartMeta {
  return PART_META_BY_KEY[part];
}

// ---------------------------------------------------------------------------
// External administration chip labels
// ---------------------------------------------------------------------------

/**
 * Pseudo job-role label used for the external-administration narrowing tag
 * (`jcf:external_admin`). It feeds the chip prefix, so an EXAD template's
 * `contact_job_*` chips read "EXAD: <field>". Also acts as the sentinel that
 * switches `jobContactPartLabel` into entity-aware mode (see below).
 */
export const EXTERNAL_ADMIN_ROLE_LABEL = "EXAD" as const;

/**
 * Entity-scoped labels for the EXAD context. A company in external
 * administration exposes THREE distinct entities on the one fanned-out
 * contact — the company itself, its primary contact person, and the
 * appointee's firm — so a single "EXAD: Full Name" is ambiguous. Under the
 * EXAD prefix each part therefore names its entity: "EXAD: Company Full
 * Name", "EXAD: Primary Contact Full Name", "EXAD: Firm". Parts absent from
 * this map fall back to their generic `shortLabel`.
 */
const EXTERNAL_ADMIN_FIELD_LABELS: Partial<Record<ContactFieldPart, string>> = {
  // The company's own record. `name` is the canonical full name; `full_name`
  // is the explicit First + Middle + Last variant — disambiguated exactly as
  // the generic labels do so the two don't read identically.
  name: "Company Full Name",
  first_name: "Company First Name",
  middle_name: "Company Middle Name",
  last_name: "Company Last Name",
  full_name: "Company Full Name (First Middle Last)",
  date_of_birth: "Company Date of Birth",
  email: "Company Email",
  phone: "Company Phone",
  abn: "Company ABN",
  acn: "Company ACN",
  address_full: "Company Address",
  address_line1: "Company Address Line 1",
  address_line2: "Company Address Line 2",
  address_suburb: "Company Suburb",
  address_state: "Company State",
  address_postcode: "Company Postcode",
  name_with_status: "Company Name with Status",
  external_admin_status: "Status",
  // The company's primary contact person.
  primary_contact_name: "Primary Contact Full Name",
  primary_contact_first_name: "Primary Contact First Name",
  primary_contact_middle_name: "Primary Contact Middle Name",
  primary_contact_last_name: "Primary Contact Last Name",
  primary_contact_full_name: "Primary Contact Full Name (First Middle Last)",
  primary_contact_email: "Primary Contact Email",
  primary_contact_phone: "Primary Contact Phone",
  // The appointee and their firm.
  external_admin_appointee: "Appointee",
  external_admin_appointee_title: "Appointee Title",
  external_admin_appointee_email: "Appointee Email",
  external_admin_appointee_phone: "Appointee Phone",
  external_admin_firm: "Firm",
  external_admin_firm_address: "Firm Address",
};

/**
 * The segment shown after the chip prefix for a job-contact basic part, e.g.
 * the "Full Name" in "Director: Full Name". When the template's prefix is the
 * EXAD sentinel the segment becomes entity-scoped (see
 * `EXTERNAL_ADMIN_FIELD_LABELS`) so the three entities are distinguishable.
 *
 * Shared by the Insert Field menu and the on-load chip-label refresh so both
 * surfaces read identically.
 */
export function jobContactPartLabel(
  part: ContactFieldPart,
  prefix: string,
): string {
  if (prefix === EXTERNAL_ADMIN_ROLE_LABEL) {
    const label = EXTERNAL_ADMIN_FIELD_LABELS[part];
    if (label) return label;
  }
  return PART_META_BY_KEY[part].shortLabel;
}

/** Human label for a contact-field key, e.g. "Job Contact — Email". */
export function contactFieldLabel(key: string): string | null {
  const binding = parseContactFieldKey(key);
  if (!binding) return null;
  const domainLabel =
    binding.domain === "firm" ? "Firm Contact" : "Job Contact";
  return `${domainLabel} — ${PART_META_BY_KEY[binding.part].label}`;
}

// ---------------------------------------------------------------------------
// Custom fields (`contact_<domain>_cf_<key>`)
// ---------------------------------------------------------------------------

/**
 * User-defined firm-contact fields, defined per `(org, category)` in the
 * `firmContactFieldDefinitions` table. Unlike basic parts (a closed
 * vocabulary) or source kinds (a fixed registry), the `<key>` here is an
 * org-authored snake_case slug, so the codec carries no key whitelist —
 * the resolver only emits keys it has a matching definition for.
 *
 * The `cf_` discriminator after the domain is collision-free: no basic
 * part is named `cf`, and no source kind starts with `cf_`, so a
 * `contact_<domain>_cf_<key>` key parses to `null` in both
 * `parseContactFieldKey` and `parseContactSourceFieldKey`.
 *
 * Mirror of the schema's `firmContactFieldTypeValidator` — kept here as a
 * literal union so this module stays dependency-free.
 */
export type FirmContactCustomFieldType =
  "text" | "number" | "currency" | "date" | "select";

const CUSTOM_FIELD_INFIX = "cf_" as const;

export interface ContactCustomFieldKey {
  domain: ContactFieldDomain;
  key: string;
}

/** Build the canonical `contact_<domain>_cf_<key>` key. */
export function buildContactCustomFieldKey(
  binding: ContactCustomFieldKey,
): string {
  return `${CONTACT_FIELD_PREFIX}${binding.domain}_${CUSTOM_FIELD_INFIX}${binding.key}`;
}

/**
 * Parse a field key into a custom-field binding, or `null` when the key
 * isn't a custom contact field. Everything after `cf_` is taken verbatim
 * as the slug (so keys with underscores round-trip cleanly).
 */
export function parseContactCustomFieldKey(
  key: string,
): ContactCustomFieldKey | null {
  if (!key.startsWith(CONTACT_FIELD_PREFIX)) return null;
  const rest = key.slice(CONTACT_FIELD_PREFIX.length);
  const split = splitContactDomain(rest);
  if (!split) return null;
  const { domain, after } = split;
  if (!after.startsWith(CUSTOM_FIELD_INFIX)) return null;
  const slug = after.slice(CUSTOM_FIELD_INFIX.length);
  if (slug.length === 0) return null;
  return { domain, key: slug };
}

/** True when a field key is a custom contact-field binding. */
export function isContactCustomFieldKey(key: string): boolean {
  return parseContactCustomFieldKey(key) !== null;
}

/**
 * Human label for a custom contact-field key. The slug alone isn't
 * descriptive, so callers pass the definition's `label` (and optionally
 * its category label) and this composes e.g.
 * "Firm Contact (Bank) — BSB". Falls back to the raw slug when no label
 * is supplied.
 */
export function contactCustomFieldLabel(
  key: string,
  label?: string,
  categoryLabel?: string,
): string | null {
  const binding = parseContactCustomFieldKey(key);
  if (!binding) return null;
  const domainLabel =
    binding.domain === "firm" ? "Firm Contact" : "Job Contact";
  const scoped = categoryLabel
    ? `${domainLabel} (${categoryLabel})`
    : domainLabel;
  return `${scoped} — ${label ?? binding.key}`;
}

// ---------------------------------------------------------------------------
// Source-data fields (`contact_<domain>_<source_kind>_<attribute>`)
// ---------------------------------------------------------------------------

/**
 * Mirrors `ContactSourceKind` in `src/lib/contact-roles.ts` (which itself
 * mirrors `contactSourceKindValidator` in
 * `convex/contacts/jobContacts/schema.ts`). Duplicated
 * here so this module stays dependency-free — both shapes must move
 * together when a new source kind is added.
 */
export type ContactSourceKind =
  | "corporate"
  | "related_party"
  | "security_interest"
  | "employee"
  | "liability"
  | "loan"
  | "ato_sro"
  | "asset_valuation";

/**
 * Display metadata for one source-field attribute. `part` is the segment
 * that follows the source kind in the canonical key, `label` is what the
 * Insert Field menu shows under the "Job Contact: <Source>" heading, and
 * `shortLabel` is the noun used when building the placeholder chip label
 * (e.g. `Owed Amount` rather than the longer menu label).
 */
export interface ContactSourceFieldMeta {
  part: string;
  label: string;
  shortLabel?: string;
}

/**
 * Per-source-kind registry of attributes that can be inserted as fields.
 * Single source of truth — extending this object is the only change
 * needed to expose a new attribute. The resolver in `fieldMapping.ts`
 * keys off the same `part` value, so the two stay in lock-step.
 */
export const CONTACT_SOURCE_FIELDS: Record<
  ContactSourceKind,
  ReadonlyArray<ContactSourceFieldMeta>
> = {
  security_interest: [
    { part: "ppsr_number", label: "PPSR Registration Number" },
    { part: "collateral_description", label: "Collateral Description" },
    { part: "collateral_class", label: "Collateral Class" },
    { part: "collateral_type", label: "Collateral Type" },
    { part: "registration_date", label: "Registration Date" },
    { part: "registration_end_date", label: "Registration End Date" },
    { part: "agreement_date", label: "Agreement Date" },
    { part: "registration_status", label: "Registration Status" },
    { part: "fixed_or_floating", label: "Fixed or Floating" },
    { part: "pmsi", label: "PMSI (Yes/No)", shortLabel: "PMSI" },
    { part: "owed_amount", label: "Owed Amount" },
    { part: "owed_amount_as_at", label: "Owed Amount As-At Date" },
    { part: "secured_party_name", label: "Secured Party Name" },
    { part: "secured_party_identifier", label: "Notice Identifier" },
    { part: "secured_party_identifier_type", label: "Notice Identifier Type" },
    { part: "secured_party_address", label: "Secured Party Address" },
  ],
  liability: [
    { part: "amount", label: "Amount Owed" },
    { part: "nature", label: "Nature of Debt" },
    { part: "date", label: "Date" },
    { part: "secured", label: "Secured (Yes/No)" },
    { part: "security_details", label: "Security Details" },
  ],
  loan: [
    { part: "amount", label: "Loan Amount" },
    { part: "outstanding_balance", label: "Outstanding Balance" },
    { part: "agreement_status", label: "Agreement Status" },
    { part: "interest_rate", label: "Annual Interest Rate" },
    { part: "repayment_terms", label: "Repayment Terms" },
    { part: "security_status", label: "Security Status" },
    { part: "date", label: "Loan Date" },
  ],
  employee: [
    { part: "role", label: "Role / Job Title" },
    { part: "employment_category", label: "Employment Category" },
    { part: "total_entitlements", label: "Total Entitlements" },
    { part: "feg_recoverable_total", label: "Est FEG Entitlement Total" },
    { part: "wages_owed", label: "Unpaid Wages" },
    { part: "super_owed", label: "Unpaid Superannuation" },
    { part: "annual_leave_owed", label: "Accrued Annual Leave" },
    { part: "leave_loading_owed", label: "Leave Loading" },
    { part: "long_service_leave_owed", label: "Accrued Long Service Leave" },
    { part: "payment_in_lieu_owed", label: "Payment in Lieu of Notice" },
    { part: "redundancy_owed", label: "Redundancy Pay" },
    { part: "hire_date", label: "Hire Date" },
    { part: "termination_date", label: "Termination Date" },
    { part: "residency_status", label: "Residency Status" },
  ],
  corporate: [
    { part: "category", label: "Category" },
    { part: "entity_type", label: "Entity Type" },
  ],
  related_party: [
    { part: "party_type", label: "Party Type" },
    { part: "relationship_category", label: "Relationship Category" },
    { part: "relationship_status", label: "Relationship Status" },
    { part: "basis_of_relationship", label: "Basis of Relationship" },
  ],
  asset_valuation: [
    { part: "asset_description", label: "Asset Description" },
    { part: "asset_type", label: "Asset Type" },
    { part: "valuation_value", label: "Valuation Amount" },
    { part: "valuation_as_at", label: "Valuation As-At Date" },
  ],
  ato_sro: [
    { part: "authority", label: "Authority Code" },
    { part: "authority_label", label: "Authority Label" },
    { part: "running_balance", label: "Running Balance" },
    { part: "running_balance_as_at", label: "Running Balance As-At Date" },
  ],
};

/** Set of valid `<source_kind>` segments — used for cheap key parsing. */
const SOURCE_KINDS: ReadonlySet<ContactSourceKind> = new Set(
  Object.keys(CONTACT_SOURCE_FIELDS) as ContactSourceKind[],
);

/** Map of `${kind}__${part}` → meta for quick label lookups. */
const SOURCE_PART_META: ReadonlyMap<string, ContactSourceFieldMeta> = (() => {
  const map = new Map<string, ContactSourceFieldMeta>();
  for (const kind of Object.keys(
    CONTACT_SOURCE_FIELDS,
  ) as ContactSourceKind[]) {
    for (const meta of CONTACT_SOURCE_FIELDS[kind]) {
      map.set(`${kind}__${meta.part}`, meta);
    }
  }
  return map;
})();

export interface ContactSourceFieldKey {
  domain: ContactFieldDomain;
  sourceKind: ContactSourceKind;
  part: string;
}

/**
 * Build the canonical `contact_<domain>_<source_kind>_<part>` key.
 * `domain` is `"job"` in V1 (firm contacts don't have job-scoped source
 * data); the slot is kept so the format mirrors the basic contact-field
 * codec and a future `firm` overlay would slot in cleanly.
 */
export function buildContactSourceFieldKey(
  binding: ContactSourceFieldKey,
): string {
  return `${CONTACT_FIELD_PREFIX}${binding.domain}_${binding.sourceKind}_${binding.part}`;
}

/**
 * Parse a field key into a source binding. Returns `null` when the key
 * isn't a contact-field source binding (so callers can fall through to
 * basic contact / canonical / custom handling).
 *
 * Discriminating from the basic contact codec is unambiguous because the
 * basic-part vocabulary is a closed set (`PART_SET`); anything that
 * starts with `contact_<domain>_` but doesn't match it is treated as a
 * source binding.
 */
export function parseContactSourceFieldKey(
  key: string,
): ContactSourceFieldKey | null {
  if (!key.startsWith(CONTACT_FIELD_PREFIX)) return null;
  const rest = key.slice(CONTACT_FIELD_PREFIX.length);
  const split = splitContactDomain(rest);
  if (!split) return null;
  const { domain, after } = split;
  // Basic contact-field keys (`contact_job_name`, etc.) are handled by
  // `parseContactFieldKey` — skip them here so the two parsers don't
  // both claim ownership.
  if (PART_SET.has(after)) return null;

  // Greedy-match the longest known source-kind prefix. Source kinds are
  // unique multi-underscore tokens (e.g. `security_interest`,
  // `asset_valuation`) so a single linear scan does the right thing
  // without a tokeniser.
  const sourceMatch = [...SOURCE_KINDS].reduce<{
    sourceKind: ContactSourceKind;
    partSegment: string;
  } | null>((best, candidate) => {
    const prefix = `${candidate}_`;
    if (!after.startsWith(prefix)) return best;
    if (best && candidate.length <= best.sourceKind.length) return best;
    return { sourceKind: candidate, partSegment: after.slice(prefix.length) };
  }, null);
  if (!sourceMatch || sourceMatch.partSegment.length === 0) return null;
  const { sourceKind, partSegment } = sourceMatch;
  if (!SOURCE_PART_META.has(`${sourceKind}__${partSegment}`)) return null;
  return { domain, sourceKind, part: partSegment };
}

/** True when a field key is a contact-source-field binding. */
export function isContactSourceFieldKey(key: string): boolean {
  return parseContactSourceFieldKey(key) !== null;
}

/**
 * Human label per source kind. Mirrors `SOURCE_KIND_META.label` from
 * `src/lib/contact-roles.ts` (kept in sync manually to keep this module
 * dependency-free).
 */
export const CONTACT_SOURCE_KIND_LABELS: Record<ContactSourceKind, string> = {
  corporate: "Corporate Structure",
  related_party: "Related Party",
  security_interest: "Security Interest",
  employee: "Employee",
  liability: "General Liability",
  loan: "Loan",
  ato_sro: "Tax Authority",
  asset_valuation: "Asset Valuation",
};

/**
 * Metadata for a single source-field part. Throws when the binding
 * refers to an unknown part — every caller should have validated the key
 * via `parseContactSourceFieldKey` first.
 */
export function getContactSourceFieldMeta(
  binding: ContactSourceFieldKey,
): ContactSourceFieldMeta {
  const meta = SOURCE_PART_META.get(`${binding.sourceKind}__${binding.part}`);
  if (!meta) {
    throw new Error(
      `Unknown contact source field: ${binding.sourceKind}.${binding.part}`,
    );
  }
  return meta;
}

/**
 * Human label for a contact-source-field key, e.g.
 * "Job Contact: Security Interest: PPSR Registration Number".
 */
export function contactSourceFieldLabel(key: string): string | null {
  const binding = parseContactSourceFieldKey(key);
  if (!binding) return null;
  const domainLabel =
    binding.domain === "firm" ? "Firm Contact" : "Job Contact";
  const sourceLabel = CONTACT_SOURCE_KIND_LABELS[binding.sourceKind];
  const meta = getContactSourceFieldMeta(binding);
  return `${domainLabel}: ${sourceLabel}: ${meta.label}`;
}

// ---------------------------------------------------------------------------
// Firm-contact REFERENCES (`firmref_<category>_<part>` / `firmref_<category>_cf_<key>`)
//
// A *reference* is the dual of the fan-out family above. The `contact_firm_*`
// codec resolves to whichever firm contact the template fans out over (one
// document per `fc:<category>` match). A `firmref_*` field instead names a
// firm-contact CATEGORY but NOT a specific contact — generation shows a
// single-select picker and binds every `firmref_<category>_*` key to that one
// chosen contact, WITHOUT multiplying the document. This lets a letter
// addressed to a director quote, say, the org member's fees inline.
//
// The `firmref_` prefix is deliberately distinct from `contact_` so a
// reference key never cross-claims with `parseContactFieldKey` /
// `parseContactSourceFieldKey`, while still being snake_case (no colons) so it
// flows through the same placeholder regexes, TipTap chip, and PDF mappings as
// every canonical key.
//
// `<category>` is a closed set (mirrors `firmContactCategoryValidator` in
// `convex/contacts/firmContacts/schema.ts` / `FirmContactCategory` in
// `src/lib/contact-roles.ts`) —
// kept here as a literal set so this module stays dependency-free. Categories
// can contain underscores (`org_member`), so parsing greedy-matches the
// longest known category prefix, exactly like the source-kind codec above.
// ---------------------------------------------------------------------------

/** Mirrors `FirmContactCategory` — keep in lock-step with the schema enum. */
export type FirmContactCategory =
  "bank" | "regulator" | "professional" | "org_member" | "referrer" | "other";

/** Closed category set, longest-first so greedy prefix matching is stable. */
const FIRM_CATEGORY_SET: ReadonlyArray<FirmContactCategory> = [
  "professional",
  "org_member",
  "regulator",
  "referrer",
  "other",
  "bank",
];

/**
 * Human labels per category. Mirrors `FIRM_CATEGORY_META.shortLabel` from
 * `src/lib/contact-roles.ts` (kept in sync manually so this module stays
 * dependency-free).
 */
export const FIRM_CATEGORY_LABELS: Record<FirmContactCategory, string> = {
  bank: "Bank",
  regulator: "Regulator",
  professional: "Professional",
  org_member: "Firm Member",
  referrer: "Referrer",
  other: "Other",
};

export const FIRM_REF_PREFIX = "firmref_" as const;

export interface FirmRefFieldKey {
  category: FirmContactCategory;
  part: ContactFieldPart;
}

export interface FirmRefCustomFieldKey {
  category: FirmContactCategory;
  key: string;
}

/** Greedy-match the longest known category prefix on a `firmref_`-stripped tail. */
function matchFirmCategory(
  tail: string,
): { category: FirmContactCategory; rest: string } | null {
  const match = FIRM_CATEGORY_SET.reduce<{
    category: FirmContactCategory;
    rest: string;
  } | null>((best, candidate) => {
    const prefix = `${candidate}_`;
    if (!tail.startsWith(prefix)) return best;
    if (best && candidate.length <= best.category.length) return best;
    return { category: candidate, rest: tail.slice(prefix.length) };
  }, null);
  if (!match || match.rest.length === 0) return null;
  return match;
}

/** Build the canonical `firmref_<category>_<part>` key. */
export function buildFirmRefFieldKey(binding: FirmRefFieldKey): string {
  return `${FIRM_REF_PREFIX}${binding.category}_${binding.part}`;
}

/** Build the canonical `firmref_<category>_cf_<key>` key. */
export function buildFirmRefCustomFieldKey(
  binding: FirmRefCustomFieldKey,
): string {
  return `${FIRM_REF_PREFIX}${binding.category}_${CUSTOM_FIELD_INFIX}${binding.key}`;
}

/**
 * Parse a `firmref_<category>_<part>` basic-field key, or `null` when the key
 * isn't a well-formed firm reference to a basic part. Custom-field keys
 * (`..._cf_...`) parse to `null` here — use `parseFirmRefCustomFieldKey`.
 */
export function parseFirmRefFieldKey(key: string): FirmRefFieldKey | null {
  if (!key.startsWith(FIRM_REF_PREFIX)) return null;
  const matched = matchFirmCategory(key.slice(FIRM_REF_PREFIX.length));
  if (!matched) return null;
  if (!isContactFieldPart(matched.rest)) return null;
  return { category: matched.category, part: matched.rest };
}

/**
 * Parse a `firmref_<category>_cf_<key>` custom-field key, or `null`. Everything
 * after `cf_` is taken verbatim as the slug so keys with underscores round-trip.
 */
export function parseFirmRefCustomFieldKey(
  key: string,
): FirmRefCustomFieldKey | null {
  if (!key.startsWith(FIRM_REF_PREFIX)) return null;
  const matched = matchFirmCategory(key.slice(FIRM_REF_PREFIX.length));
  if (!matched) return null;
  if (!matched.rest.startsWith(CUSTOM_FIELD_INFIX)) return null;
  const slug = matched.rest.slice(CUSTOM_FIELD_INFIX.length);
  if (slug.length === 0) return null;
  return { category: matched.category, key: slug };
}

/** True when a key is a firm-reference basic-field key. */
export function isFirmRefFieldKey(key: string): boolean {
  return parseFirmRefFieldKey(key) !== null;
}

/** True when a key is a firm-reference custom-field key. */
export function isFirmRefCustomFieldKey(key: string): boolean {
  return parseFirmRefCustomFieldKey(key) !== null;
}

/** True when a key is any firm-reference key (basic or custom). */
export function isAnyFirmRefKey(key: string): boolean {
  return isFirmRefFieldKey(key) || isFirmRefCustomFieldKey(key);
}

/** The firm category a `firmref_*` key references, or `null` if not a ref key. */
export function firmRefKeyCategory(key: string): FirmContactCategory | null {
  return (
    parseFirmRefFieldKey(key)?.category ??
    parseFirmRefCustomFieldKey(key)?.category ??
    null
  );
}

/** Human label for a firm-reference basic-field key, e.g. "Firm Member (ref) — Name". */
export function firmRefFieldLabel(key: string): string | null {
  const binding = parseFirmRefFieldKey(key);
  if (!binding) return null;
  return `${FIRM_CATEGORY_LABELS[binding.category]} (ref) — ${PART_META_BY_KEY[binding.part].label}`;
}

/**
 * Human label for a firm-reference custom-field key. The slug alone isn't
 * descriptive, so callers pass the definition's `label`; falls back to the raw
 * slug. E.g. "Firm Member (ref) — Fees".
 */
export function firmRefCustomFieldLabel(
  key: string,
  label?: string,
): string | null {
  const binding = parseFirmRefCustomFieldKey(key);
  if (!binding) return null;
  return `${FIRM_CATEGORY_LABELS[binding.category]} (ref) — ${label ?? binding.key}`;
}
